// src/pages/sales/PageOrderBook.jsx
import { useState, useEffect } from "react";
import * as api from "../../apiService";

const PAGE_SIZE = 30;

function fmt(n) {
  return Number(n || 0).toLocaleString("en-AE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function CompanySection({ label, data, loading, error, open, onToggle }) {
  const [page, setPage] = useState(0);

  const groups     = data?.groups || [];
  const grandTotal = data?.grandTotal || 0;
  const totalOrders = groups.reduce((s, g) => s + g.orderCount, 0);
  const totalLines  = groups.reduce((s, g) => s + g.lineCount,  0);

  // Flatten to rows: one row per line item
  const rows = [];
  groups.forEach(group => {
    group.orders.forEach(order => {
      order.lines.forEach(line => {
        rows.push({
          partyName:  group.partyName,
          orderRef:   order.ref,
          orderDate:  order.date,
          dueDate:    order.dueDate,
          item:       line.item,
          pendingQty: line.pendingQty,
          rate:       line.rate,
          value:      line.value,
          isNegative: line.isNegative,
        });
      });
    });
  });

  // Reset to first page when data changes
  useEffect(() => { setPage(0); }, [data]);

  const pageCount   = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const visibleRows = rows.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  return (
    <div style={{ marginBottom: 20 }}>
      {/* Section header / toggle */}
      <div
        onClick={onToggle}
        style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer",
          background: "#f3f4f6", borderRadius: 6, padding: "8px 14px",
          border: "1px solid #e5e7eb", userSelect: "none" }}>
        <span style={{ fontSize: 11, color: "#9ca3af" }}>{open ? "▾" : "▸"}</span>
        <span style={{ fontWeight: 700, fontSize: 14 }}>{label}</span>
        {data && !loading && (
          <span style={{ fontSize: 11, color: "#6b7280", marginLeft: 4 }}>
            {groups.length} customer{groups.length !== 1 ? "s" : ""} &middot; {totalOrders} order{totalOrders !== 1 ? "s" : ""} &middot; {totalLines} item{totalLines !== 1 ? "s" : ""}
          </span>
        )}
        {loading && <span style={{ fontSize: 11, color: "#9ca3af" }}>Loading…</span>}
        {data && !loading && (
          <span style={{ marginLeft: "auto", fontWeight: 700, fontSize: 13 }}>
            AED {fmt(grandTotal)}
          </span>
        )}
      </div>

      {/* Section body */}
      {open && (
        <div style={{ marginTop: 4 }}>
          {error && <div className="alert-warn" style={{ margin: "8px 0" }}>Error: {error}</div>}
          {loading && <div style={{ color: "#aaa", fontSize: 12, padding: 12 }}>Loading from Tally…</div>}
          {!loading && !error && data && (
            rows.length === 0 ? (
              <div style={{ textAlign: "center", color: "#aaa", padding: 24, fontSize: 13 }}>
                No open orders
              </div>
            ) : (
              <>
              <div className="table-wrap">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Customer Name</th>
                      <th>Order Ref</th>
                      <th>Order Date</th>
                      <th>Due Date</th>
                      <th>Item / Product</th>
                      <th style={{ textAlign: "right" }}>Pending Qty</th>
                      <th style={{ textAlign: "right" }}>Rate</th>
                      <th style={{ textAlign: "right" }}>Value (AED)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleRows.map((r, i) => (
                      <tr key={i} style={{ background: r.isNegative ? "#fff7ed" : "white" }}>
                        <td style={{ fontWeight: 600, fontSize: 12 }}>{r.partyName}</td>
                        <td style={{ fontFamily: "monospace", fontSize: 12 }}>{r.orderRef}</td>
                        <td style={{ fontSize: 12, color: "#555" }}>{r.orderDate}</td>
                        <td style={{ fontSize: 12, color: "#555" }}>{r.dueDate}</td>
                        <td style={{ fontSize: 12 }}>
                          {r.isNegative && (
                            <span style={{ fontSize: 10, color: "#dc2626", fontWeight: 700,
                              background: "#fee2e2", borderRadius: 3, padding: "1px 4px", marginRight: 5 }}>
                              OVER-DELIV
                            </span>
                          )}
                          {r.item}
                        </td>
                        <td style={{ textAlign: "right", fontSize: 12, whiteSpace: "nowrap" }}>{r.pendingQty}</td>
                        <td style={{ textAlign: "right", fontSize: 12 }}>{r.rate ?? "—"}</td>
                        <td style={{ textAlign: "right", fontSize: 12, fontWeight: 600,
                          color: r.isNegative ? "#dc2626" : "inherit" }}>
                          {r.value != null ? fmt(r.value) : "—"}
                        </td>
                      </tr>
                    ))}
                    <tr style={{ borderTop: "2px solid #374151" }}>
                      <td colSpan={7} style={{ textAlign: "right", fontWeight: 700, fontSize: 13,
                        padding: "8px 12px 8px 0" }}>
                        Total
                      </td>
                      <td style={{ textAlign: "right", fontWeight: 700, fontSize: 14 }}>
                        {fmt(grandTotal)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              {pageCount > 1 && (
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 8,
                  fontSize: 12, color: "#6b7280" }}>
                  <button className="btn" style={{ fontSize: 11, padding: "2px 10px" }}
                    onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}>
                    ← Prev
                  </button>
                  <span>Page {page + 1} of {pageCount} ({rows.length} rows)</span>
                  <button className="btn" style={{ fontSize: 11, padding: "2px 10px" }}
                    onClick={() => setPage(p => Math.min(pageCount - 1, p + 1))} disabled={page === pageCount - 1}>
                    Next →
                  </button>
                </div>
              )}
              </>
            )
          )}
        </div>
      )}
    </div>
  );
}

export function PageOrderBook({ myPerms }) {
  const [amiData,    setAmiData]    = useState(null);
  const [amtData,    setAmtData]    = useState(null);
  const [amiLoading, setAmiLoading] = useState(false);
  const [amtLoading, setAmtLoading] = useState(false);
  const [amiError,   setAmiError]   = useState(null);
  const [amtError,   setAmtError]   = useState(null);
  const [amiOpen,    setAmiOpen]    = useState(true);
  const [amtOpen,    setAmtOpen]    = useState(true);

  function loadAmi(bust = false) {
    setAmiLoading(true); setAmiError(null);
    (bust ? api.refreshOrderBook("AMI") : api.fetchOrderBook("AMI"))
      .then(setAmiData)
      .catch(e => setAmiError(e.message))
      .finally(() => setAmiLoading(false));
  }

  function loadAmt(bust = false) {
    setAmtLoading(true); setAmtError(null);
    (bust ? api.refreshOrderBook("AMT") : api.fetchOrderBook("AMT"))
      .then(setAmtData)
      .catch(e => setAmtError(e.message))
      .finally(() => setAmtLoading(false));
  }

  function refresh() { loadAmi(true); loadAmt(true); }

  useEffect(() => { refresh(); }, []); // eslint-disable-line

  function expandAll()   { setAmiOpen(true);  setAmtOpen(true);  }
  function collapseAll() { setAmiOpen(false); setAmtOpen(false); }

  const loading = amiLoading || amtLoading;

  return (
    <>
      <div className="page-header">
        <div className="page-header-left">
          <div className="page-title">Order Book</div>
          <div className="page-subtitle">Live open sales orders from Tally</div>
        </div>
        <div className="page-header-right" style={{ display: "flex", gap: 8 }}>
          <button className="btn" style={{ fontSize: 12 }} onClick={expandAll}>Expand All</button>
          <button className="btn" style={{ fontSize: 12 }} onClick={collapseAll}>Collapse All</button>
          <button className="btn" style={{ fontSize: 12 }} onClick={refresh} disabled={loading}>
            {loading ? "Loading…" : "↻ Refresh"}
          </button>
        </div>
      </div>

      <CompanySection
        label="AMI — Industries"
        data={amiData}
        loading={amiLoading}
        error={amiError}
        open={amiOpen}
        onToggle={() => setAmiOpen(o => !o)}
      />
      <CompanySection
        label="AMT — Trading"
        data={amtData}
        loading={amtLoading}
        error={amtError}
        open={amtOpen}
        onToggle={() => setAmtOpen(o => !o)}
      />
    </>
  );
}
